import { useLoaderData } from 'react-router';
import { useHydrated } from 'remix-utils';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    DetailMobileAppChrome,
    IslandFreeClosing,
} from '~/ui-rocco/pages/detail-shared';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { JoinArchiProSection } from '~/ui-rocco/pages/details-page/revamp/sections/JoinArchiProSection';
import { GetMatchedBand } from '~/ui-rocco/pages/details-page/revamp/sections/GetMatchedBand';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { BookMeetingAccordionProvider } from './hooks/use-book-meeting-accordion';
import { ProjectDetailsMagicIsland } from './magic-island/ProjectDetailsMagicIsland';
import { ProjectHeroSection } from './sections/ProjectHeroSection';
import { ProjectSummarySection } from './sections/ProjectSummarySection';
import { ProjectCreditsRow } from './sections/ProjectCreditsRow';
import { ProjectStorySection } from './sections/ProjectStorySection';
import { ProjectAccordionSection } from './sections/ProjectAccordionSection';
import { ProjectGallerySection } from './sections/ProjectGallerySection';
import { AboutProfessionalSection } from './sections/AboutProfessionalSection';
import { RelatedProjectsSection } from './sections/RelatedProjectsSection';
import { SimilarProjectsSection } from './sections/SimilarProjectsSection';
import { ProjectEnquiryPrompt } from './sections/ProjectEnquiryPrompt';

/**
 * Revamped editorial project details page.
 *
 * Built section-by-section (P2–P10) per
 * `plans/BC-4505-details-page-revamp.md` → "Project details — section breakdown".
 * Sections are added incrementally, top to bottom; the loader is shared with the
 * legacy page and stays unchanged. Rendered in place of the legacy page behind
 * the `details_revamp_2026` feature flag (or `?revamp=1` in local dev).
 */
export const ProjectPageRevamp = () => {
    const { project } = useLoaderData<typeof loader>();
    const hydrated = useHydrated();
    const { mobile } = useAppDisplayModeContext();
    // The featured editorial (ProjectStorySection) is optional. Only widen the
    // lead-in to the design's L gap when it will actually render — otherwise the
    // credits → accordion gap stays at the standard spacing (no empty band).
    const hasFeaturedArticle = Boolean(
        (
            project as typeof project & {
                FeaturedArticle?: { Link: string } | null;
            }
        ).FeaturedArticle,
    );

    return (
        <BookMeetingAccordionProvider>
            <ProjectDetailsMagicIsland>
                <DetailMobileAppChrome />
                <main
                    id="main-content"
                    data-testid="project-page-revamp"
                    // overflow-x-clip contains the gallery modules' 4px full-bleed
                    // so it never triggers a horizontal scrollbar (it clips
                    // without becoming a scroll container, so the fixed island and
                    // vertical scroll are unaffected).
                    className="overflow-x-clip bg-[#181818]"
                >
                    {/* Sections P2–P10 land here, top to bottom. The content block
                    sits on the dark editorial background; the hero is a
                    full-bleed image and the closing brings its own sections. */}
                    <ProjectHeroSection />
                    <SectionSpacer height={48} mobileHeight={24} unit="rem" />
                    <ProjectSummarySection />
                    <SectionSpacer height={64} mobileHeight={24} unit="rem" />
                    <ProjectCreditsRow />
                    {/* Generous L lead-in to the featured editorial — only when
                        that (optional) section renders; otherwise the standard
                        64px gap to the accordion. */}
                    <SectionSpacer
                        height={hasFeaturedArticle ? 160 : 64}
                        mobileHeight={hasFeaturedArticle ? 59 : 32}
                        unit="rem"
                    />
                    <ProjectStorySection />
                    <SectionSpacer height={56} mobileHeight={28} unit="rem" />
                    <ProjectAccordionSection />
                    <SectionSpacer height={80} mobileHeight={40} unit="rem" />
                    <ProjectGallerySection />
                    {/* End-of-gallery enquiry prompt — Book a Meeting + the
                        website referral, rendered where the images have just
                        made the case (the legacy banner's slot, now
                        enquiry-led on both devices). */}
                    <SectionSpacer height={120} mobileHeight={48} unit="rem" />
                    <ProjectEnquiryPrompt
                        heading="Planning something similar?"
                        body="Book a meeting with {supplier} to talk through your project."
                    />
                    <SectionSpacer height={120} mobileHeight={48} unit="rem" />
                    <AboutProfessionalSection />
                    {/* Mobile folds the related carousel into About-the-
                        Professional, so this gap collapses there (the next
                        spacer carries the gap down to Similar Projects). */}
                    <SectionSpacer height={120} mobileHeight={0} unit="rem" />
                    <RelatedProjectsSection />
                    <SectionSpacer height={120} mobileHeight={48} unit="rem" />
                    {/* Keyed on the project: this route does not remount
                        between projects, and clicking a tile in this very grid
                        lands on another project in the same category, so its
                        paging state has to be tied to the project id. */}
                    <SimilarProjectsSection key={project.ID} />
                    <SectionSpacer height={160} mobileHeight={59} unit="rem" />
                    <ProjectClosing />
                </main>
                {/* Long page — same control as the browse grids and the product
                    detail page. Dark: the page's own ground is #181818. */}
                {hydrated && !mobile ? <BackToTop isDark /> : null}
            </ProjectDetailsMagicIsland>
        </BookMeetingAccordionProvider>
    );
};

/**
 * The page's closing region: Get Matched, then the sign-up band. The editorial
 * closing that used to sit here repeated the homepage's hero and intro, so it
 * is gone; Get Matched stays because it is the page's only remaining
 * lead-capture exit, and the account band closes behind it.
 */
const ProjectClosing = () => {
    const { getMatchedStyleImages } = useLoaderData<typeof loader>();
    // A member has no account to create, so the band and the air that sets it
    // apart from Get Matched both go; the closing ends on Get Matched instead.
    const { isLoggedIn } = useUserLoggedIn();

    return (
        // bg-white backs the whole closing so the sign-up band (built for the
        // white page) never lets the page's dark #181818 show through the
        // sub-pixel seams between fixed-aspect sections as stray lines.
        <IslandFreeClosing className="bg-white">
            <SectionSpacer
                height={100}
                mobileHeight={64}
                className="bg-white"
                unit="rem"
            />
            <GetMatchedBand
                enquiryType="project"
                styleImages={getMatchedStyleImages}
            />
            {!isLoggedIn && (
                <>
                    <SectionSpacer
                        height={100}
                        mobileHeight={64}
                        className="bg-white"
                        unit="rem"
                    />
                    <JoinArchiProSection />
                </>
            )}
            <SectionSpacer
                height={100}
                mobileHeight={64}
                className="bg-white"
                unit="rem"
            />
        </IslandFreeClosing>
    );
};
